import { Link } from "react-router-dom";
import { Facebook, Linkedin, Mail, MessageCircle } from "lucide-react";
import { LogoMark } from "@/components/brand/Logo";
import { SITE, channels } from "@/config/site";
import { NAV } from "@/content/navigation";

const ICONS = { whatsapp: MessageCircle, email: Mail, linkedin: Linkedin, facebook: Facebook };

const SocialLink = ({ c }) => {
  const Icon = ICONS[c.key];
  const cls = "flex h-12 w-12 items-center justify-center rounded-full border border-white/15 transition-colors duration-300";
  if (!c.href) {
    return (
      <span className={`${cls} cursor-not-allowed text-white/40`} title={`${c.label} — coming soon`} aria-label={`${c.label} (coming soon)`} data-testid={`footer-social-${c.key}`}>
        <Icon className="h-4 w-4" />
      </span>
    );
  }
  return (
    <a href={c.href} target="_blank" rel="noopener noreferrer" aria-label={c.label} data-testid={`footer-social-${c.key}`} className={`${cls} text-white hover:border-gold hover:text-gold-light`}>
      <Icon className="h-4 w-4" />
    </a>
  );
};

export const Footer = () => (
  <footer className="grain relative overflow-hidden bg-ink pt-20 text-white" data-testid="site-footer">
    <div className="shell relative z-[2]">
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <div className="flex items-center gap-4">
            <LogoMark className="h-12 w-12" />
            <p className="font-display text-3xl font-medium tracking-tight">VION TECH</p>
          </div>
          <p className="mt-8 max-w-md font-display text-2xl font-light leading-snug text-white/90 sm:text-3xl">{SITE.tagline}</p>
          <p className="mt-4 eyebrow text-gold">{SITE.slogan}</p>
        </div>
        <nav className="lg:col-span-3" aria-label="Footer">
          <p className="eyebrow text-white/60">Explore</p>
          <ul className="mt-5 space-y-3">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link to={n.to} className="text-[15px] text-white/80 transition-colors hover:text-gold-light" data-testid={`footer-link-${n.to === "/" ? "home" : n.to.slice(1)}`}>
                  {n.full || n.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className="lg:col-span-3">
          <p className="eyebrow text-white/60">Connect</p>
          <div className="mt-5 flex flex-wrap gap-3">
            {channels().map((c) => <SocialLink key={c.key} c={c} />)}
          </div>
          <Link to="/contact" className="mt-8 inline-block border-b border-gold/60 pb-1 text-[15px] text-gold-light transition-colors hover:border-gold-light" data-testid="footer-contact-cta">
            Start a conversation →
          </Link>
        </div>
      </div>

      <div className="mt-16 border-t border-white/10 py-8">
        <p className="max-w-3xl text-[13px] leading-relaxed text-white/60" data-testid="footer-disclaimer">
          VION Tech provides technology education, consulting and global opportunity services. Specific outcomes may vary based on individual circumstances and applicable requirements.
        </p>
        <p className="mt-4 text-[13px] text-white/60">© {new Date().getFullYear()} {SITE.legalName}. All rights reserved.</p>
      </div>
    </div>
    <p aria-hidden="true" className="pointer-events-none relative z-[2] -mb-[0.22em] select-none text-center font-display text-[24vw] font-light leading-none tracking-tighter text-white/[0.04]">
      VION
    </p>
  </footer>
);
